/**
 * Transição entre as telas do cadastro e do onboarding (06/10/2026), pela View
 * Transitions API do navegador.
 *
 * Só o conteúdo se move: ao avançar, o atual sai para a esquerda e o novo
 * entra pela direita; ao voltar, o contrário. A moldura fica parada — logo,
 * voltar, o cartão branco (que só ajusta a altura), a barra de progresso (o
 * passo ativo desliza até o novo) e os botões de baixo. Telas cheias (vídeo
 * de boas-vindas, "A sua jornada é única") e a passagem entre o login e o
 * cadastro trocam por fusão. As peças que ficam paradas levam o
 * `view-transition-name` em `novo/kit.tsx`.
 *
 * A direção vai num atributo da raiz (`data-transicao`), lido pelo CSS. O
 * voltar do navegador ou do gesto do celular também conta: o React Router
 * aplica a transição no POP quando a ida usou `viewTransition`, e o ouvinte de
 * `popstate` daqui decide a direção pelo índice do histórico.
 *
 * Sem suporte no navegador, a tela troca como antes. Com "reduzir movimento",
 * sem animação.
 */
import { flushSync } from 'react-dom';
import type { NavigateFunction, NavigateOptions } from 'react-router-dom';

export type Direcao = 'avanco' | 'retorno' | 'fusao';

const DURACAO = '300ms';
const CURVA = 'cubic-bezier(0.22, 1, 0.36, 1)';

const CSS = `
::view-transition-group(*) { animation-duration: ${DURACAO}; animation-timing-function: ${CURVA}; }
::view-transition-old(eva-conteudo) { animation: ${DURACAO} ${CURVA} both eva-sai-para-esquerda; }
::view-transition-new(eva-conteudo) { animation: ${DURACAO} ${CURVA} both eva-entra-pela-direita; }
:root[data-transicao="retorno"]::view-transition-old(eva-conteudo) { animation-name: eva-sai-para-direita; }
:root[data-transicao="retorno"]::view-transition-new(eva-conteudo) { animation-name: eva-entra-pela-esquerda; }
:root[data-transicao="fusao"]::view-transition-old(eva-conteudo) { animation-name: eva-esmaece; }
:root[data-transicao="fusao"]::view-transition-new(eva-conteudo) { animation-name: eva-aparece; }
@keyframes eva-sai-para-esquerda { to { opacity: 0; transform: translateX(-18%); } }
@keyframes eva-entra-pela-direita { from { opacity: 0; transform: translateX(18%); } }
@keyframes eva-sai-para-direita { to { opacity: 0; transform: translateX(18%); } }
@keyframes eva-entra-pela-esquerda { from { opacity: 0; transform: translateX(-18%); } }
@keyframes eva-esmaece { to { opacity: 0; } }
@keyframes eva-aparece { from { opacity: 0; } }
@media (prefers-reduced-motion: reduce) {
  ::view-transition-group(*), ::view-transition-old(*), ::view-transition-new(*) { animation: none !important; }
}
`;

const temDocumento = typeof document !== 'undefined';
const suporta = () => temDocumento && typeof (document as Document & { startViewTransition?: unknown }).startViewTransition === 'function';
const movimentoReduzido = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

function marcar(direcao: Direcao) {
  if (temDocumento) document.documentElement.dataset.transicao = direcao;
}

/** Índice do histórico do React Router (`history.state.idx`). */
const indiceDoHistorico = (estado: unknown) => (estado as { idx?: number } | null)?.idx;

let ultimoIndice: number | undefined;

if (temDocumento && !document.getElementById('eva-transicao')) {
  const style = document.createElement('style');
  style.id = 'eva-transicao';
  style.textContent = CSS;
  document.head.appendChild(style);
  ultimoIndice = indiceDoHistorico(window.history.state);
  // Captura: roda antes do ouvinte do React Router, que começa a transição.
  window.addEventListener(
    'popstate',
    (e) => {
      const novo = indiceDoHistorico(e.state);
      marcar(novo !== undefined && ultimoIndice !== undefined && novo > ultimoIndice ? 'avanco' : 'retorno');
      ultimoIndice = novo;
    },
    true,
  );
}

/** Anota o índice depois de uma navegação PUSH/REPLACE, para o próximo POP saber a direção. */
function lembrarIndice() {
  setTimeout(() => {
    ultimoIndice = indiceDoHistorico(window.history.state);
  }, 0);
}

/** Navega entre rotas com a transição na direção dada (avanço por padrão). */
export function navegarCom(navigate: NavigateFunction, para: string, direcao: Direcao = 'avanco', opcoes: NavigateOptions = {}) {
  marcar(direcao);
  void navigate(para, { ...opcoes, viewTransition: suporta() && !movimentoReduzido() });
  lembrarIndice();
}

/** Antes de um `<Link viewTransition>`: só marca a direção. */
export function marcarDirecao(direcao: Direcao) {
  marcar(direcao);
  lembrarIndice();
}

/**
 * Troca de estado dentro da mesma página (os passos do onboarding) com a
 * transição: o React precisa terminar a renderização dentro do callback, daí o
 * `flushSync`.
 */
export function transicionar(direcao: Direcao, atualizar: () => void) {
  if (!suporta() || movimentoReduzido()) {
    atualizar();
    return;
  }
  marcar(direcao);
  (document as Document & { startViewTransition: (cb: () => void) => unknown }).startViewTransition(() => flushSync(atualizar));
}
